import { Link } from 'react-router-dom';
import type { DashboardProjectCard, ProjectHealth } from '../../lib/dashboardApi';
import { formatCurrency, formatDate } from '../../lib/format';
import { HealthBadge } from './HealthBadge';

interface ProjectStatusGridProps {
  projects: DashboardProjectCard[];
}

// Red (off track) first so the COO sees problems at the top.
const HEALTH_ORDER: Record<ProjectHealth, number> = {
  OFF_TRACK: 0,
  AT_RISK: 1,
  ON_TRACK: 2,
};

const HEALTH_BAR: Record<ProjectHealth, string> = {
  OFF_TRACK: 'bg-red-500',
  AT_RISK: 'bg-amber-500',
  ON_TRACK: 'bg-emerald-500',
};

/**
 * Compact project status cards (CLE-256, Secțiunea 5). Sorted with the most
 * at-risk projects first. Progress is approximated from open tasks since the
 * backend card payload does not carry a stored completion percentage.
 */
export function ProjectStatusGrid({ projects }: ProjectStatusGridProps) {
  const sorted = [...projects].sort((a, b) => HEALTH_ORDER[a.health] - HEALTH_ORDER[b.health]);

  return (
    <section className="min-w-0 rounded-xl border border-border bg-card p-4 shadow-card sm:p-5">
      <h3 className="text-sm font-semibold text-foreground">Proiecte</h3>

      {sorted.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground/70">Niciun proiect activ.</p>
      ) : (
        // grid-cols-1 (minmax(0, 1fr)) and min-w-0 on the cards: without them a
        // long project name sizes the column on phones and the cards overflow.
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {sorted.map((project) => {
            // Proxy progress: more open tasks → less complete. Capped to a sane range.
            const progress = Math.max(
              5,
              Math.min(95, 100 - Math.min(project.openTaskCount, 20) * 5),
            );
            return (
              <Link
                key={project.id}
                to={`/projects/${project.id}`}
                className="flex min-w-0 flex-col rounded-lg border border-border/60 bg-muted/20 p-4 transition hover:-translate-y-0.5 hover:border-border hover:bg-muted/30"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h4 className="truncate text-sm font-semibold text-foreground">
                      {project.name}
                    </h4>
                    {project.owner && (
                      <p className="truncate text-xs text-muted-foreground">{project.owner.name}</p>
                    )}
                  </div>
                  <HealthBadge health={project.health} />
                </div>

                <div className="mt-3">
                  <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                    <div
                      className={`h-full rounded-full ${HEALTH_BAR[project.health]}`}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>

                <dl className="mt-3 space-y-1 text-xs text-muted-foreground">
                  <div className="flex justify-between">
                    <dt>Termen</dt>
                    <dd className="text-foreground">{formatDate(project.deadline)}</dd>
                  </div>
                  {project.budget !== null && (
                    <div className="flex justify-between">
                      <dt>Buget</dt>
                      <dd className="text-foreground">
                        {formatCurrency(project.budget, project.budgetCurrency)}
                      </dd>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <dt>Sarcini deschise</dt>
                    <dd className="text-foreground">{project.openTaskCount}</dd>
                  </div>
                </dl>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}
